import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeCompanyUsers,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { soldConfigInitialValues } from '../AutomationSoLDSettingsSlideout/AutomationSoLDSettingsSlideout';
import {
  CampaignType,
  SoldConfigurationsType,
} from '@mm/state/model/CampaignsModel';
import { useConnectSlack } from '../../../../features/beta/modules/Integrations/NativeIntegrations/components/SlackIntegrations/use-connect-slack';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useFormik } from 'formik';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { DropdownOption } from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import css from './AutomationSalesTeamNotifications.module.css';
import { Heading, Switch, Tooltip } from '@mm/components';
import { CloseOutlined, SettingOutlined } from '@ant-design/icons';
import { ReactComponent as SlackIcon } from 'assets/slack.svg';
import {
  AutomationReportingEventSelector,
  getCampaignActiveReportingOptionId,
  getIsCampaignReportingStatusActive,
  HandIcon,
} from '../../../../features/beta/modules/Automations/AutomationEdit/AutomationEditEmailSold';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useSOLDNotifications } from '../AutomationSoLDSettingsSlideout/use-SOLD-notifications';
import { useViewActions } from '@mm/state/actions/viewActions';
import { equals } from 'lodash/fp';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { EmailRed, SmsNotificationIcon } from '@mm/icons';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CampaignResourcePayload } from '@mm/state/controllers/CampaignsController';

export interface IAutomationSalesTeamNotifications {
  menuType: SlideOutMenuType;
  campaignId: number;
  onSuccess: (automation: CampaignResourcePayload) => void;
}

export const AutomationSalesTeamNotifications = ({
  menuType,
  campaignId = -1,
  onSuccess = () => {},
}: IAutomationSalesTeamNotifications) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);

  const campaign = useSelector(state =>
    selectCampaignById(state, campaignId || -1),
  );

  const companyUsers = useSelector(activeCompanyUsers);

  const getRemappedCompanyUsers = () =>
    companyUsers.map(usr => ({
      id: usr.id,
      name: `${usr.firstName} ${usr.lastName}`,
      key: `${usr.id}`,
    }));

  const [users, setUsers] = useState(getRemappedCompanyUsers());

  useEffect(
    () => {
      const newUsers = getRemappedCompanyUsers();
      setUsers(newUsers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(companyUsers)],
  );

  const usersCRUDActions = useUsersCRUDActions();

  const {
    id: userId,
    userAttributes: { desktopNotifications, hideMobileDownload },
  } = useSelector((state: StoreState) => state.session.session);

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const {
    canActivateSmsNotification,
    canActivateSlackNotification,
    canActivateEmailNotification,
    hasSMSNotificationContent,
    hasSMSNotificationPhones,
    hasEmailNotificationContent,
    hasEmailNotificationEmails,
  } = useSOLDNotifications(campaign);

  const safeInitialValues = useMemo(() => {
    let initValues: any = soldConfigInitialValues;

    if (typeof campaign?.soldConfigurations === 'string') {
      if (Object.keys(JSON.parse(campaign?.soldConfigurations)).length) {
        initValues = JSON.parse(campaign?.soldConfigurations);
      }
    } else if (
      campaign?.soldConfigurations &&
      Object.keys(campaign.soldConfigurations).length
    ) {
      initValues = campaign.soldConfigurations;
    }

    return initValues;
  }, [campaign, users]);

  const [slack, setSlack] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.slack?.active,
  );
  const [email, setEmail] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.email?.active,
  );
  const [sms, setSms] = useState(
    campaign?.soldConfigurations?.unicorn?.notifications?.sms?.active,
  );
  const { connectSlack, retrieveSlackAccounts, slackAccounts } =
    useConnectSlack();

  const formik = useFormik<typeof soldConfigInitialValues>({
    initialValues: safeInitialValues,
    onSubmit: async values => {},
  });

  const active =
    formik.values.stopSendingAfterReply ||
    Object.values(formik.values.unicorn).some(value => value > 0) ||
    Object.values(formik.values.donkey).some(value => value > 0);

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;

  // Do all the needed transformations to the formik values before sending to BE
  const withNotifications = (sold: typeof soldConfigInitialValues) => {
    const clone = JSON.parse(JSON.stringify(sold));

    delete clone.unicorn.notifications.sms;
    delete clone.unicorn.notifications.slack;

    clone.unicorn.notifications.sms = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.sms,
      active: sms,
    };
    clone.unicorn.notifications.slack = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.slack,
      active: slack,
    };

    clone.unicorn.notifications.email = {
      ...campaign?.soldConfigurations?.unicorn?.notifications?.email,
      active: email,
    };

    return clone as SoldConfigurationsType;
  };

  const onBeforeClose = async () => {
    return;
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const viewActions = useViewActions();

  const { setRef, close: closeSlideout } = useSlideoutOnClose(
    onBeforeClose,
    menuType,
  );

  const companyUserIdsToNotify: number[] = [];

  useEffect(
    () => {
      const newUsers = getRemappedCompanyUsers();
      setUsers(newUsers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(companyUsers), JSON.stringify(companyUserIdsToNotify)],
  );

  const updateMobileCompanyUsersToNotify = (keys: string[]) => {
    if (keys.length === 0) {
      formik.setFieldValue('unicorn.notifications.mobilePush.active', false);
    }
    const keysAsNumbers = keys.map(k => Number(k));
    if (
      !equals(
        keysAsNumbers,
        formik.values.unicorn.notifications?.mobilePush?.to,
      )
    ) {
      formik.setFieldValue(
        'unicorn.notifications.mobilePush.to',
        keysAsNumbers,
      );
    }
  };

  const updateDesktopCompanyUsersToNotify = (keys: string[]) => {
    if (keys.length === 0) {
      formik.setFieldValue('unicorn.notifications.desktopPush.active', false);
    }
    const keysAsNumbers = keys.map(k => Number(k));
    if (
      !equals(
        keysAsNumbers,
        formik.values.unicorn.notifications?.desktopPush?.to,
      )
    ) {
      formik.setFieldValue(
        'unicorn.notifications.desktopPush.to',
        keysAsNumbers,
      );
    }
  };

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const getSwitchCss = (type?: string) => {
    let isActive = false;

    switch (type) {
      case 'sms':
        isActive = !!sms;
        break;
      case 'slack':
        isActive = !!slack;
        break;
      case 'reply':
        isActive = !!formik.values.stopSendingAfterReply;
        break;
      case 'desktop':
        isActive = !!formik.values.unicorn.notifications?.desktopPush?.active;
        break;
      case 'mobile':
        isActive = !!formik.values.unicorn.notifications?.mobilePush?.active;
        break;
      case 'email':
        isActive = !!email;
        break;
      default:
        return;
    }

    return {
      container: isActive ? css.contentSwitchActive : css.contentSwitchInactive,
      thumb: isActive ? css.contentThumbActive : css.contentThumbInactive,
      label: isActive
        ? css.contentSwitchLabelActive
        : css.contentSwitchLabelInactive,
    };
  };

  useEffect(() => {
    retrieveSlackAccounts();
  }, []);

  const [isReportingActive, setIsReportingActive] = useState(
    !!getIsCampaignReportingStatusActive(campaign),
  );

  const handleReportingActivateToggle = () => {
    setIsReportingActive(!isReportingActive);
  };

  const renderSmsSwitch = (
    <Switch
      checked={!!sms}
      disabled={!canActivateSmsNotification || !isReportingActive}
      onChange={() => {
        setSms(!sms);
      }}
      css={{
        container: getSwitchCss('sms')?.container,

        thumb: getSwitchCss('sms')?.thumb,
        label: getSwitchCss('sms')?.label,
      }}
    />
  );

  const [event, setEvent] = useState<string | number>(
    getCampaignActiveReportingOptionId(campaign) || '',
  );
  const handleSalesTeamNodeSelectorChange = (opt: DropdownOption) => {
    setEvent(opt.id);
  };

  const withActivation = (sold: typeof soldConfigInitialValues) => {
    const clone = JSON.parse(JSON.stringify(sold));

    clone.unicorn.notifications.rule.open = 0;
    clone.unicorn.notifications.rule.reply = 0;
    clone.unicorn.notifications.rule.click = 0;
    clone.unicorn.notifications.defaultConfiguration = false;

    if (isReportingActive && event in clone.unicorn.notifications.rule) {
      clone.unicorn.notifications.rule[event] = 1;
    }

    return clone as SoldConfigurationsType;
  };

  const onSave = async () => {
    setBusy(true);
    try {
      if (campaignId) {
        const res = await campaignCRUDActions.updateCampaign(
          {
            id: campaignId,
            soldConfigurations: withActivation(
              withNotifications(formik.values),
            ),
          } as unknown as CampaignType,
          chatbotId,
        );

        onSuccess(res);

        successToast('Your changes have been saved.', 'top-left');
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setDirty(true);
    } else {
      onSave();
    }
  }, [sms, slack, email, event, isReportingActive]);

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <HandIcon className={css.icon} color="#40A9FF" />
        <Heading size={3}>Introduce to Sales Team & Notifications</Heading>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <Switch
          checked={isReportingActive}
          label="Activate"
          disabled={busy}
          onChange={handleReportingActivateToggle}
          css={{
            container: css.switch,
            thumb: active ? css.thumbActive : css.thumbInactive,
            label: active ? css.switchLabelActive : css.switchLabelInactive,
          }}
        />

        <div className={css.selectorContainer}>
          <span>If a lead</span>
          <AutomationReportingEventSelector
            value={event}
            disabled={!isReportingActive || busy}
            onChange={handleSalesTeamNodeSelectorChange}
          />
        </div>

        <div className={css.block}>
          <div className={css.subtitle}>Notify me and sales team via: </div>
          <div className={css.notificationRow}>
            <Tooltip
              title={
                !canActivateSlackNotification
                  ? 'You have to set up Workspace, Channel and Message in order to activate'
                  : ''
              }
            >
              <Switch
                checked={!!slack}
                disabled={!canActivateSlackNotification || !isReportingActive}
                onChange={() => {
                  setSlack(!slack);
                }}
                css={{
                  container: getSwitchCss('slack')?.container,

                  thumb: getSwitchCss('slack')?.thumb,
                  label: getSwitchCss('slack')?.label,
                }}
              />
            </Tooltip>
            <SlackIcon className={css.notificationIcon} />
            <div>Slack</div>
            {!slackAccounts?.length ? (
              <PrimaryButton
                onClick={connectSlack}
                className={css.secondaryButton}
              >
                Connect
              </PrimaryButton>
            ) : (
              <SettingOutlined
                onClick={() =>
                  viewActions.showSlideoutMenu(
                    'automationSoLDSlackNotificationSettings',
                    { campaignId },
                  )
                }
                className={css.notificatoinSettingIcon}
              />
            )}
          </div>

          <div className={css.notificationRow}>
            <Tooltip
              title={
                !canActivateEmailNotification ? (
                  <div>
                    {' '}
                    You have to enter a{' '}
                    {!canActivateEmailNotification
                      ? 'message and recipient email address'
                      : !hasEmailNotificationContent
                      ? 'message'
                      : 'recipient email address'}{' '}
                    in order to activate
                  </div>
                ) : (
                  ''
                )
              }
            >
              <Switch
                checked={!!email}
                onChange={() => {
                  setEmail(!email);
                }}
                disabled={!canActivateEmailNotification || !isReportingActive}
                css={{
                  container: getSwitchCss('email')?.container,

                  thumb: getSwitchCss('email')?.thumb,
                  label: getSwitchCss('email')?.label,
                }}
              />
            </Tooltip>
            <EmailRed
              className={css.notificationIcon}
              style={{ width: '20px' }}
            />
            <div>Email</div>
            <SettingOutlined
              onClick={() =>
                viewActions.showSlideoutMenu(
                  'automationSoLDEmailNotificationSettings',
                  { campaignId },
                )
              }
              className={css.notificatoinSettingIcon}
            />
          </div>

          <div className={css.notificationRow}>
            {hasSMSNotificationPhones ? (
              renderSmsSwitch
            ) : (
              <Tooltip
                title={
                  <div>
                    {' '}
                    You have to enter a{' '}
                    {[
                      !hasSMSNotificationContent && 'text message',
                      !hasSMSNotificationPhones && 'phone number',
                    ]
                      .filter(Boolean)
                      .join(' and a')}{' '}
                    in order to activate
                  </div>
                }
              >
                {renderSmsSwitch}
              </Tooltip>
            )}

            <SmsNotificationIcon
              className={classNames(css.notificationIcon, css.sms)}
            />
            <span className={css.notificationTitle}>SMS</span>

            <SettingOutlined
              onClick={() =>
                viewActions.showSlideoutMenu(
                  'automationSoLDSmsNotificationSettings',
                  { campaignId },
                )
              }
              className={css.notificatoinSettingIcon}
            />
          </div>
        </div>
      </Menu.Content>
    </Menu>
  );
};
